import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { dayShort } from '../lib/when';
import useMediaQuery from '../hooks/useMediaQuery';
import PageBar, { DESKTOP_QUERY } from '../components/PageBar';
/*
  **The pieces this screen and Inventory Counts are both built from**, moved out
  on 5 September 2026. Cycle Details is the same drawing as Inventory Counts,
  grouped by rep instead of by region. Before this they were two programs, and
  each of those was written twice, once per width: four copies of one screen.
*/
import {
  CYCLE_STATS, CycleStat, CycleStrip, GroupGrid, LocationList, PanelHead,
  RowButton, cardColumns, locationName,
} from './cycle/parts';
import { useAuth } from '../components/AuthContext';
import { useToast } from '../components/ToastContext';
import { DeskFrame, PhoneFrame } from '../components/AppFrame';
import { menuFor } from '../lib/menu';
import ManagerHome from './home/ManagerHome';
import { warehouseRepMap } from '../lib/assignments';
import { fetchAll } from '../lib/save';
import { cycleLineTotals } from '../lib/cycleTotals';
/*
  **My Counts is one screen for everybody**, which is rule 23. There used to be a
  copy of its loader in this file, a copy of the same thing in
  `pages/admin/MyCounts.jsx`, and they had drifted: this one built its
  per-location figures without the two fields the card needs to say how much of a
  sheet is filled in, so every card here said "No lines on this sheet yet"
  whether it was true or not.
*/
import MyCounts from './cycle/MyCounts';
/*
  **Tasks was a near-copy of the rep's**, and both were a copy of each other.
  One screen now, for the manager and the rep, at every width.
*/
import Tasks from './cycle/Tasks';
/*
  **A manager has their own history too**, and had no way to it. Group C's
  drawings have had History in the manager's menu since 31 August; it arrives
  today with the screen itself, which is one screen for the manager and the rep.
*/
import History from './cycle/History';
// **A manager exports their own counting too**, and had no way to. Last of the
// entries `src/lib/menu.jsx` was withholding until its page existed.
import MyExports from './cycle/MyExports';
import UsersAccess from './admin/Settings/UsersAccess';
import CountViewOnlyModal from '../components/CountViewOnlyModal';
import PageLoading from '../components/PageLoading';
import SaveFailedBar from '../components/SaveFailedBar';
import SystemShell from '../components/SystemShell';
import Appearance from './settings/Appearance';

// The pile nobody covers. It is not a rep, so it needs a key that cannot
// collide with one, and a kind label of its own on the card.
const UNASSIGNED_KEY = '__unassigned__';

// A sheet's stage in the words the rest of OnHand prints.
const STAGE_WORDS = { not_started: 'Not started', in_progress: 'In progress', submitted: 'Submitted', approved: 'Approved' };


function fmtVal(v) {
  if (!v) return null;
  if (v >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M';
  if (v >= 1000)    return '$' + Math.round(v / 1000) + 'K';
  return '$' + Math.round(v);
}

// **The menu moved out of this file on 5 September 2026**, into
// `src/lib/menu.jsx`, with the admin's and the rep's. This screen had two of the
// six: a flat one-tier menu on a desk and a separate four-item dock below 1024.

/*
  **There was a THIRD copy of the status pill here**, removed 5 September 2026.
  It wrote "Not Started" and "In Progress" in title case where the rest of the
  app writes them in sentence case, and it reached for a colour class the
  stylesheet has under another name. Nothing on this screen could show the
  disagreement, because no screen puts two of them side by side. The one in
  `src/pages/admin/adminUtils.jsx` is the pill now, everywhere.
*/

// My Counts, which mirrors the rep's screen one for one.
// Tasks, which a manager raises for an admin and reads the answers to.
// Main component
export default function ManagerDashboard() {
  /*
    **The pop-up messages, which this page lost on 5 September 2026** when its
    `useToast` line was removed and one use of `toast` was left behind: pressing
    View only on Cycle Details drew the read-only window with `toast={toast}`
    and the whole page fell over to the crash screen (the audit after the
    rebrand, issue 2, 18 September 2026).
  */
  const toast = useToast();
  // Which layout tree to mount. Same breakpoint the top bar uses, so the
  // mounted tree is always the one whose bar is on screen.
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  /*
    **How many cards sit side by side**, from Group C's approved drawings: three
    on a desk and on a tablet on its side, two on a tablet upright, one on
    anything narrower. Counted here rather than left to the stylesheet, because
    the locations of an opened card are laid UNDER THE ROW that card is in.
  */
  const isTwoUp = useMediaQuery('(min-width: 768px)');
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [tab, setTabRaw] = useState(() =>
    // **Home is where the app opens**, for a manager as for everybody. Somebody
    // who left it on another page still comes back to it.
    sessionStorage.getItem('managerTab') || 'home');
  /*
    **Three filters, and they narrow the same list**, from Group C's approved
    drawing of this screen: a search line, a region picker and a status picker.
    This screen had none of them, so a manager looking for one location among
    133 was scrolling for it.

    **The search reads a location's name AND its rep's**, which is what the
    drawing's own words say: "Search a location or a rep".
  */
  const [locationSearch, setLocationSearch] = useState('');
  const [regionFilter, setRegionFilter]     = useState('');
  const [progressFilter, setProgressFilter] = useState('all');
  // How much of each sheet is filled in, for the Lines column.
  const [lineCounts, setLineCounts] = useState({});
  // Desktop rep accordion: one rep open at a time (null = none)
  const [expandedRep, setExpandedRep] = useState(null);
  // Desktop accordion: opening a rep closes any other; clicking the open one collapses it
  function toggleRep(repId) { setExpandedRep(prev => (prev === repId ? null : repId)); }

  function setTab(t) { sessionStorage.setItem('managerTab', t); setTabRaw(t); }

  // Renders the action button/control for a location row on the Cycle Details page.
  // Matches My Counts button sizing & behavior.
  /*
    The button at the end of a location's row.

    **A manager may look at somebody else's count and may not edit it**, which is
    the one place this differs from the admin's version of the same row: an admin
    gets an Override that opens the sheet, a manager gets a read-only view.

    The button's SHAPE comes from `RowButton` in cycle/parts.jsx, so the two
    screens cannot drift on it again. They had: this one wrote "View Count" and
    "Continue Count" in title case and used a pale blue fill where the approved
    drawing shows white.
  */
  function renderCycleLocationAction(p) {
    const submittedOrApproved = p.status === 'submitted' || p.status === 'approved';
    // A regional office is counted by the managers of its region too (the
    // owner's word, 18 September 2026), so it opens for counting, not View only.
    const ownedByOther = !submittedOrApproved && p.rep_id && profile?.id && p.rep_id !== profile.id
                         && !p.warehouse?.allows_concurrent_counting;

    if (ownedByOther) {
      return (
        <RowButton tone="caution"
          title={'This count is open with ' + (p.rep?.full_name || 'another rep')}
          onClick={() => setCycleViewOnlyCount({
            id: p.id, status: p.status, submitted_at: p.submitted_at, rep_id: p.rep_id,
            warehouse: p.warehouse,
            owner: p.rep ? { full_name: p.rep.full_name } : null,
          })}>
          View only
        </RowButton>
      );
    }
    if (submittedOrApproved) {
      return (
        <RowButton tone="quiet" onClick={() => navigate('/count/' + p.id)}>
          View count
        </RowButton>
      );
    }
    return (
      <RowButton onClick={() => navigate('/count/' + p.id)}>
        {p.status === 'not_started' ? 'Start count' : 'Continue count'}
      </RowButton>
    );
  }

  const [cycle, setCycle]       = useState(null);
  const [progress, setProgress]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [valuations, setValuations] = useState({}); // { count_id: dollar_value }
  const [cycleViewOnlyCount, setCycleViewOnlyCount] = useState(null); // for Cycle Details tapping other-rep counts

  useEffect(() => { loadData(); }, []);

  async function signOut() {
    await supabase.auth.signOut();
    navigate('/login');
  }

  async function loadData() {
    setLoading(true);
    const { data: cycleData } = await supabase
      .from('count_cycles').select('*').eq('status', 'open').single();

    setCycle(cycleData || null);

    if (cycleData) {
      /*
        **Four requests that waited on each other now go out together.**
        Corrected 8 September 2026, with the administrator's home, after the
        owner filmed signing in and watched a blank page for about four seconds.
        None of these asks a question that depends on another one's answer.
      */
      const [
        { data: counts },
        { data: myRegions, error: regionErr },
        { data: reps },
        whReps,
      ] = await Promise.all([
        /*
          **Every sheet, a page at a time, and with the clean name** (the audit
          after the rebrand, issues 22 and 29, 18 September 2026). It stopped at
          500 with nothing to say so, which a site of more than 500 locations
          would have met with some of them simply missing; and without
          `display_name` and `region_prefix` Cycle Details printed the raw
          "DFW-..." name where the admin's printed the clean one.
        */
        fetchAll(() => supabase
          .from('inventory_counts')
          .select('id, status, submitted_at, approved_at, rep_id, warehouse:warehouses(id, name, display_name, region_prefix, region_id, allows_concurrent_counting, region:regions(name))')
          .eq('cycle_id', cycleData.id).order('status').order('id')),
        /*
          **Which regions this manager covers, and where that comes from.**

          Fixed 5 September 2026. Group C found this on 31 August while drawing the
          manager's screens, and it was verified against the live database again
          before it was touched.

          OnHand keeps a manager's regions in two places. `user_regions` is a proper
          list of who covers what, written by the Users & Access screen. `profiles.region`
          is a plain text box on the person's record. **This screen read the text box**,
          split it on commas, and matched region NAMES against it.

          What that cost, counted on 5 September 2026 against the open cycle's 286
          locations:

            Jamy Stull    covers Louisiana, Oklahoma and San Antonio, which is 133
                          locations. Her text box says "Oklahoma". She saw 22.
            Charlie Cude  covers Dallas, East Texas and Houston, which is 153
                          locations. His text box says "Austin", which is not one of
                          the six regions at all. **He saw NOTHING.**

          Nothing is wrong in the database and nothing was lost. A manager simply could
          not see most of the work they are responsible for, and one of them could not
          see any of it, and neither screen said anything was missing.

          **It reads `user_regions` now**, by region id rather than by name, so a region
          that gets renamed cannot quietly empty somebody's screen the way a typed name
          can. **`profiles.region` no longer exists**: it was removed later the same
          day, on the owner's word, because two answers to one question is what
          caused this.
        */
        supabase.from('user_regions').select('region_id').eq('user_id', profile?.id),
        fetchAll(() => supabase.from('profiles').select('id, full_name').order('id')),
        warehouseRepMap(),
      ]);
      const mine = regionErr ? null : (myRegions || []).map(r => r.region_id);

      const repMap = {};
      for (const r of reps || []) repMap[r.id] = r;

      /*
        **A manager with no regions sees none, not all.** The old filter said
        "if the list is empty, show everything", which meant a manager whose text
        box was blank was shown the whole company. Covering nothing and covering
        everything are opposite facts and they cannot share a branch.

        The one case that still shows everything is a FAILED read, because a
        manager should not be told they cover nothing on the strength of a
        request that did not come back.
      */
      const scoped = (counts || [])
        .filter(c => mine === null || mine.includes(c.warehouse?.region_id))
        .map(c => ({
          ...c,
          rep: c.rep_id ? repMap[c.rep_id] : null,
          allReps: (whReps[c.warehouse?.id] || []).map(id => repMap[id]).filter(Boolean),
        }));

      setProgress(scoped);

      /*
        What each sheet is worth, and how much of it is filled in.

        **This was reading every line of the cycle into the browser** until 8
        September 2026, a thousand rows at a time, three requests in a row for
        the open cycle's 2,530 lines. Before that, on 5 September, it read only
        the first thousand and printed a confident wrong total. The adding up is
        done by the database now, in one request, and the browser is handed one
        row per sheet.

        **A line with no quantity is a line still to count.** Zero is a real
        answer somebody typed; blank is nobody having been there yet, which is
        rule 9, and it is now applied in one place rather than in two copies.
      */
      const allIds = scoped.map(c => c.id);
      if (allIds.length) {
        /*
          **The database adds this up now.** Rewritten 8 September 2026 with the
          administrator's home: reading every line of the cycle into the browser
          was three requests one after another for the open cycle's 2,530 lines,
          and nothing was on screen until the last of them landed.

          `onlyIds` keeps this to the locations this manager's screen is showing,
          which is what the old request did by listing them, so an estimated
          value never covers a location that is not on the page.
        */
        const totals = await cycleLineTotals(cycleData.id, allIds);
        /*
          **A read that did not work leaves the numbers alone**, rather than
          printing zeroes and telling a manager nothing has been counted.
        */
        if (!totals.failed) {
          setValuations(totals.valuations);
          setLineCounts(totals.lineCounts);
        }
      }
    }
    setLoading(false);
  }

  const stats = {
    not_started: progress.filter(p => p.status === 'not_started').length,
    in_progress:  progress.filter(p => p.status === 'in_progress').length,
    submitted:    progress.filter(p => p.status === 'submitted').length,
    approved:     progress.filter(p => p.status === 'approved').length,
  };
  const total = progress.length;
  const pct   = total > 0 ? Math.round((stats.submitted + stats.approved) / total * 100) : 0;

  /*
    Home's numbers, counted off the same `progress` this screen already holds.

    **"Who is behind" counts what a rep has NOT finished**, because that is the
    thing a manager can act on. The percentage goes on the line under it: the
    count says how much is left and the percentage says how they are doing.
  */
  const homeBehind = useMemo(() => {
    const by = new Map();
    let unassigned = 0;
    for (const p of progress) {
      const reps = p.allReps?.length ? p.allReps : null;
      if (!reps) { unassigned++; continue; }
      // Keyed by the person, not the name: the row opens THAT rep's card in
      // Cycle Details, and two reps can share a name.
      for (const rep of reps) {
        if (!by.has(rep.id)) by.set(rep.id, { key: rep.id, name: rep.full_name || 'A rep', total: 0, done: 0, left: 0 });
        const r = by.get(rep.id);
        r.total++;
        if (p.status === 'submitted' || p.status === 'approved') r.done++; else r.left++;
      }
    }
    const all = [...by.values()].map(r => ({ ...r, pct: r.total ? Math.round(r.done / r.total * 100) : 0 }));
    all.sort((a, b) => b.left - a.left || a.pct - b.pct);
    return { rows: all.slice(0, 3), unassigned };
  }, [progress]);

  // A manager can hold locations of their own, and usually does not.
  const homeMine = useMemo(() => progress
    .filter(p => p.allReps?.some(r => r.id === profile?.id))
    // The clean name and the stage in the words the rest of OnHand uses: it
    // printed the raw "DFW-..." name and "not started" in lower case with the
    // underscore swapped for a space (issue 21, 18 September 2026).
    .map(p => ({ id: p.id, name: locationName(p) || 'A location',
                 done: p.status === 'not_started' ? 0 : 1,
                 sub: STAGE_WORDS[p.status] || p.status })), [progress, profile]);

  /*
    **A row on Home opens the thing it names** (the audit after the rebrand,
    issue 21, 18 September 2026). "Who is behind" handed over a name and the
    handler ignored it, so every row opened Cycle Details with nothing open;
    "Nobody assigned" handed over a key that was not the real one. Now it opens
    that rep's card, or the Nobody assigned card, with the filters cleared so
    everything in it shows. The footer opens Cycle Details as it was.
  */
  const goToOpenCard = useRef(false);
  function openRepFromHome(key) {
    if (key) {
      setLocationSearch(''); setRegionFilter(''); setProgressFilter('all');
      setExpandedRep(key);
      goToOpenCard.current = true;
    }
    setTab('overview');
  }
  // **And the page goes to it.** The card opens where it sits in the grid,
  // which on a phone is below the bottom of the screen, so pressing the row
  // would have looked like nothing happened. The panel carries a top margin
  // for the bar, so it does not land underneath it.
  useEffect(() => {
    if (!goToOpenCard.current || tab !== 'overview') return;
    goToOpenCard.current = false;
    requestAnimationFrame(() => document.querySelector('.region-block')?.scrollIntoView({ block: 'start' }));
  }, [tab, expandedRep]);

  const regionNames = useMemo(() => {
    const set = new Set(progress.map(p => p.warehouse?.region?.name).filter(Boolean));
    return [...set].sort();
  }, [progress]);

  const searchText = locationSearch.trim().toLowerCase();
  const filteredProgress = progress.filter(p => {
    if (progressFilter !== 'all' && p.status !== progressFilter) return false;
    if (regionFilter && p.warehouse?.region?.name !== regionFilter) return false;
    if (!searchText) return true;
    const names = (p.allReps || []).map(r => r?.full_name || '').join(' ');
    return ((p.warehouse?.name || '') + ' ' + names).toLowerCase().includes(searchText);
  });

  /*
    The cards, one per rep, with Unassigned first.

    **A location with two reps on it appears on both their cards**, which is why
    the cards' totals add up to more than the cycle's on a site that shares
    locations. That is the honest answer to "how much does this person have to
    do", and the cycle's own total is on the strip above.

    **Unassigned leads**, because it is the group that needs somebody to act:
    nobody is going to count those locations unless a manager gives them out.

    **The locations inside come out alphabetically**, which they did not: they
    arrived in whatever order the database handed them back.
  */
  const repGroups = useMemo(() => {
    const by = new Map();
    for (const p of filteredProgress) {
      const reps = (p.allReps || []).filter(Boolean);
      if (reps.length === 0) {
        if (!by.has(UNASSIGNED_KEY)) by.set(UNASSIGNED_KEY, { key: UNASSIGNED_KEY, kind: 'Nobody assigned', name: 'Unassigned', rows: [] });
        by.get(UNASSIGNED_KEY).rows.push(p);
      } else {
        for (const r of reps) {
          if (!by.has(r.id)) by.set(r.id, { key: r.id, kind: 'Rep', name: r.full_name, rows: [] });
          const g = by.get(r.id);
          if (!g.rows.some(c => c.id === p.id)) g.rows.push(p);
        }
      }
    }
    for (const g of by.values()) g.rows.sort((a, b) => locationName(a).localeCompare(locationName(b)));
    const all = [...by.values()];
    const unassigned = all.filter(g => g.key === UNASSIGNED_KEY);
    const people = all.filter(g => g.key !== UNASSIGNED_KEY).sort((a, b) => a.name.localeCompare(b.name));
    return [...unassigned, ...people];
  }, [filteredProgress]); // eslint-disable-line

  // The menu, once, for both widths. A manager's areas and pages come from
  // `src/lib/menu.jsx` and the same list drives the two-tier bar on a desk and
  // the dock below 1024, so the two can no longer say different things.
  const AREAS = menuFor('manager');



  /*
    **A page loading keeps the shell**, rule 35 and Group H's approved drawing:
    the app is fine, the page is fine, it simply has not arrived. Until 10
    September 2026 this returned a spinner on a bare canvas, so for as long as
    the first read took, OnHand looked like it had nothing in it and offered no
    way anywhere.

    **It is `SystemShell` rather than a fourth copy of the bar**, so the menu
    here is the app's one menu and draws at both widths without this file
    knowing how. Same component the stopped screens use.
  */
  if (loading) return (
    <SystemShell>
      <PageLoading tall />
    </SystemShell>
  );

  /*
    **Cycle Details, built once and rendered at both widths**, to Group C's
    approved drawings, 5 September 2026.

    Until today this screen existed twice: a desk version with a completion ring,
    an estimated value and four chips, and a separate held version with its own
    compact card, its own rep rows and its own location rows. They had already
    drifted: on a desk opening a rep closed every other rep, and below 1024 each
    one toggled on its own and they all started shut, so the same screen behaved
    two ways depending on what you were holding.

    **It is the same screen as the admin's Inventory Counts, grouped by rep
    rather than by region**, so it is built from the same pieces in
    `src/pages/cycle/parts.jsx`. A manager cannot close a cycle and has no Review
    Hub, so it carries neither button; everything else is the same drawing.
  */
  const cycleDetailsView = !cycle ? (
    <div className="card" style={{ maxWidth: 480, padding: 32, textAlign: 'center' }}>
      <div className="oh-title" style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>No open count</div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>There is no count open at the moment. An administrator opens the next one.</div>
    </div>
  ) : (
    <>
      {/*
        **The cycle strip.** It replaces a card 190 pixels tall carrying a
        completion ring, the estimated value and four status chips, and it is
        rule 5: the live numbers sit on hairlines beside the name of the thing
        they describe, so the screen opens on the reps rather than on a summary
        of them.

        **The four numbers change shape below 768**, a row of labelled numbers on
        a desk and a strip of four cells on anything narrower, which is what the
        drawings show and what stops them running off the edge of a phone.
      */}
      <PageBar
        twoRows
        metaFullWidth={!isTwoUp}
        title={<>{cycle.name} <span className="pill pill-app" style={{ marginLeft: 8 }}>Cycle open</span></>}
        /*
          **The regions are named on a desk and dropped on a phone**, which is
          what the drawings show. "133 locations in Louisiana, Oklahoma, San
          Antonio" is the useful line when there is room for it: a manager covers
          some of the company and not all of it, and the screen should say which
          part. On a phone it costs two lines, and the percentage is the thing
          worth those pixels.
        */
        subtitle={isTwoUp
          ? total + ' location' + (total !== 1 ? 's' : '')
            + (regionNames.length ? ' in ' + regionNames.join(', ') : ' in your regions')
          : pct + '% done - ' + total + ' location' + (total !== 1 ? 's' : '')}
        meta={isTwoUp ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <CycleStat n={pct + '%'} label="Complete" dot="var(--info)" />
            {CYCLE_STATS.map(c => (
              <CycleStat key={c.key} n={stats[c.key]} label={c.label} dot={c.dot}
                on={progressFilter === c.key}
                onClick={() => setProgressFilter(f => f === c.key ? 'all' : c.key)} />
            ))}
          </span>
        ) : (
          <CycleStrip stats={stats} filter={progressFilter} onFilter={setProgressFilter} />
        )}
        controls={
          <>
            <input className="tb-search" placeholder="Search a location or a rep"
              value={locationSearch} onChange={e => setLocationSearch(e.target.value)} />
            <select className={'tb-select' + (regionFilter ? ' set' : '')}
              value={regionFilter} onChange={e => setRegionFilter(e.target.value)}>
              <option value="">All regions</option>
              {regionNames.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className={'tb-select' + (progressFilter !== 'all' ? ' set' : '')}
              value={progressFilter} onChange={e => setProgressFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="not_started">Not started</option>
              <option value="in_progress">In progress</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
            </select>
          </>
        }
      />

      {/* Rep cards, an accordion: opening one closes any other, and its
          locations are laid under the ROW that card is in rather than under the
          card, so the cards beside it do not jump. */}
      <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 10 }}><span className="oh-ground-tag">Reps</span></div>
      {repGroups.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
          No locations match the current filter.
        </div>
      ) : (
        <GroupGrid
          groups={repGroups}
          columns={cardColumns(isDesktop, isTwoUp)}
          openKey={expandedRep}
          onToggle={toggleRep}
          valueOf={rows => {
            const v = rows.reduce((sum, p) => sum + (valuations[p.id] || 0), 0);
            return v > 0 ? fmtVal(v) : null;
          }}
          renderPanel={g => (
            <div className="region-block" style={{ marginBottom: 14, scrollMarginTop: 96 }}>
              <PanelHead name={g.key === UNASSIGNED_KEY ? 'Nobody assigned' : g.name} count={g.rows.length} />
              <div className="region-detail">
                {/* **No Rep column here**, which is the one difference from the
                    admin's version of this list: every row in a person's own
                    panel belongs to them, so naming them on each row says the
                    same thing sixty times. */}
                <LocationList
                  rows={g.rows}
                  lineCounts={lineCounts}
                  dayOf={dayShort}
                  action={renderCycleLocationAction}
                  empty="This rep holds no locations in this count."
                />
              </div>
            </div>
          )}
        />
      )}

      {progressFilter !== 'all' && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button className="btn btn-outline" onClick={() => setProgressFilter('all')}>Clear the filter, show all</button>
        </div>
      )}
    </>
  );

  // The navigation the frames draw (rebrand step 4, src/components/AppFrame.jsx):
  // the same menu and the same page this screen already had.
  const nav = {
    areas: AREAS, activeKey: tab, onSelect: setTab,
    userName: profile?.full_name, email: profile?.email,
    roleLabel: 'Manager', onSignOut: signOut,
  };

  const desktopLayout = (
    <DeskFrame nav={nav}    >
          {/* **"That did not save", with the button to try it again.** Group H's
              approved bar, built 10 September 2026. It draws nothing at all
              unless a save has just failed for want of a connection, which is
              almost always. Under the page bar and above the work, so nothing a
              person is reading moves out from under them. */}
          <SaveFailedBar />

          {tab === 'home' && (
            <ManagerHome
              profile={profile} cycle={cycle} stats={stats} total={total} pct={pct}
              regionNames={regionNames}
              behindRows={homeBehind.rows} unassignedCount={homeBehind.unassigned}
              myCounts={homeMine}
              onOpenCycle={() => setTab('overview')}
              onOpenReps={openRepFromHome}
              onOpenMyCounts={() => setTab('mycounts')}
              onOpenCount={id => navigate('/count/' + id)}
            />
          )}

          {tab === 'overview' && cycleDetailsView}

          {tab === 'mycounts' && <MyCounts cycle={cycle} profile={profile} navigate={navigate} />}
          {tab === 'tasks'    && <Tasks profile={profile} />}
          {tab === 'history'  && <History profile={profile} navigate={navigate} />}
          {tab === 'reports'  && <MyExports profile={profile} />}
          {/* **The one Settings screen a manager has**, added 8 September 2026.
              It draws itself narrowed: their own regions' people and locations,
              and nothing they are not allowed to change. */}
          {tab === 'users_access' && <UsersAccess />}
          {tab === 'appearance' && <Appearance />}



    </DeskFrame>
  );

  //  TABLET LAYOUT 
  // Uses overview/mycounts/tasks tabs only. Item Catalog and Branding stay desktop-only.
  // **The manager had the same clamp the admin did, smaller.** It listed three
  // page keys and sent everything else to Cycle Details, so a manager on Home
  // below 1024 was pushed off it. There is no clamp now: the held layout renders
  // the same page the desk does.
  const tabletTab = tab;

  /* Topbar: logo + greeting */
  /* Floating glass header - identity only; Sign Out lives in the dock */
  /* Content */
  /* Detached glass dock - floats above content, which scrolls under it */
  const tabletLayout = (
    <PhoneFrame nav={nav}
    >
        {/* The same bar below 1024. A component added to one of a screen's two
            layout trees and not the other is missing with nothing saying so,
            which is exactly what happened to the connection bar on this day. */}
        <SaveFailedBar />

        <div>
          {tabletTab === 'home' && (
            <ManagerHome
              profile={profile} cycle={cycle} stats={stats} total={total} pct={pct}
              regionNames={regionNames}
              behindRows={homeBehind.rows} unassignedCount={homeBehind.unassigned}
              myCounts={homeMine}
              onOpenCycle={() => setTab('overview')}
              onOpenReps={openRepFromHome}
              onOpenMyCounts={() => setTab('mycounts')}
              onOpenCount={id => navigate('/count/' + id)}
            />
          )}
          {tabletTab === 'mycounts' && <MyCounts cycle={cycle} profile={profile} navigate={navigate} />}
          {tabletTab === 'tasks'    && <Tasks profile={profile} />}
          {tabletTab === 'history'  && <History profile={profile} navigate={navigate} />}
          {tabletTab === 'reports'  && <MyExports profile={profile} />}
          {tabletTab === 'users_access' && <UsersAccess />}
          {tabletTab === 'appearance' && <Appearance />}
          {tabletTab === 'overview' && cycleDetailsView}
        </div>
    </PhoneFrame>
  );

  return (
    <>
      {/* Only the tree for the current layout is mounted. Hiding one with CSS
          alone left it rendering: its pages still portalled their controls into
          the visible top bar, so any page reachable from both trees put its
          control band in there twice (Settings, and the manager's My Counts).
          Same query PageBar tests, imported rather than retyped. */}
      <div className="rep-desktop-only">{isDesktop && desktopLayout}</div>
      <div className="rep-mobile-only">{!isDesktop && tabletLayout}</div>
      {cycleViewOnlyCount && (
        <CountViewOnlyModal
          count={cycleViewOnlyCount}
          profile={profile}
          toast={toast}
          onClose={() => setCycleViewOnlyCount(null)}
        />
      )}
    </>
  );
}
